import { Divider } from '@mui/material';
import { nanoid } from 'nanoid';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { InfoWrapper, MediaUpload, RHFSelect, RHFTextField } from 'components';
import { CDAStrutturale } from 'constants/difetti';
import {
  CampateSoggetteACollasso,
  ClasseProgetto,
  FieldType,
  MediaGroup,
  UploadMedia,
  VulnerabilitaStrutturaleBassa,
} from 'constants/inspections';
import { difettositaOptions } from 'constants/selectOptions';
import { useInspectionPath } from 'hooks';
import { calculateDifettositaComplessiva } from 'pages/Inspections/CreateInspection/utils/extractDefectivenessInfo';
import { useInspectionSelector } from 'stores';
import { GridWrap } from './Accordion5CriticalAspectsL1.styles';
import CondCriticheVulnerabilitaSismica from './sections/CondCriticheVulnerabilitaSismica';
import CondCriticheVulnerabilitaStruttura from './sections/CondCriticheVulnerabilitaStruttura';
import ElCriticiVulnerabilitaSismica from './sections/ElCriticiVulnerabilitaSismica';
import ElCriticiVulnerabilitaStrutturale from './sections/ElCriticiVulnerabilitaStrutturale';
import ElVulnerabilitaSismica from './sections/ElVulnerabilitaSismica';
import TabellaRiepilogativa from './sections/TabellaRiepilogativa';
import useCDARecap from './useCDARecap';
import { INFO } from '../../../../config/info';

const Accordion5CriticalAspectsL1 = () => {
  const { watch, setValue } = useFormContext();
  const { schedaDifettiTrigger, inspectionDisabled } = useInspectionSelector();
  const { isViewingInspection } = useInspectionPath();
  const triggerWatch = watch('l1t2a5_trigger');
  const watchNormaProgetto = watch('normaProgetto');

  const { handleCDAPresence } = useCDARecap();
  // ELEMENTI CRITICI - VULNERABILITA STRUTTURALE E FONDAZIONALE
  useEffect(() => {
    handleCDAPresence(
      'elementoCriticoStruttura',
      'elCriticiVulnerabilitaStrutturaleTipologiaElemento',
      'elementiCriticiVulnerabilitaStrutturaleFondazionale'
    );
  }, [triggerWatch, schedaDifettiTrigger]);

  // ELEMENTI CRITICI - VULNERABILITA SISMICA

  useEffect(() => {
    handleCDAPresence(
      'elementoCriticoSismica',
      'elCriticiVulnerabilitaSismicaTipologiaElemento',
      'elementiCriticiVulnerabilitaSismica'
    );
  }, [triggerWatch, schedaDifettiTrigger]);

  // CONDIZIONI CRITICHE - VULNERABILITA STRUTTURALE E FONDAZIONALE

  useEffect(() => {
    handleCDAPresence(
      'condizioneCriticaStruttura',
      'elCondizioniCriticheVulnerabilitaStrutturaTipologiaElemento',
      'condizioniCriticheVulnerabilitaStrutturale'
    );
  }, [triggerWatch, schedaDifettiTrigger]);

  // CONDIZIONI CRITICHE - VULNERABILITA SISMICA

  useEffect(() => {
    handleCDAPresence(
      'condizioneCriticaSismica',
      'elCondizioniCriticheVulnerabilitaSismicaTipologiaElemento',
      'condizioniCriticheVulnerabilitaSismica'
    );
  }, [triggerWatch, schedaDifettiTrigger]);

  useEffect(() => {
    setValue('l1t2a5_trigger', nanoid());
  }, []);

  useEffect(() => {
    if (watchNormaProgetto !== ClasseProgetto.ClasseC) {
      setValue('vulnerabilitaStrutturaleBassa', '');
    }
  }, [watchNormaProgetto]);

  // Gestione logica Difettosità complessiva
  const riepilogoStrutturaTable = watch('riepilogoStrutturaTable');
  const { difettositaStrutturale, difettositaSismica } =
    calculateDifettositaComplessiva(riepilogoStrutturaTable);

  const difettositaStrutturaleEffettiva = watch(
    'arbitraryStructuralFoundationalDefectiveness'
  );
  const difettositaSismicaEffettiva = watch('arbitrarySeismicDefectiveness');

  const previousStrutturale = useRef();
  const previousSismica = useRef();

  useEffect(() => {
    if (
      previousStrutturale.current === undefined &&
      !difettositaStrutturaleEffettiva &&
      difettositaStrutturale
    ) {
      setValue(
        'arbitraryStructuralFoundationalDefectiveness',
        difettositaStrutturale
      );
    } else if (
      difettositaStrutturaleEffettiva === previousStrutturale.current
    ) {
      setValue(
        'arbitraryStructuralFoundationalDefectiveness',
        difettositaStrutturale
      );
    }

    previousStrutturale.current = difettositaStrutturale;
  }, [difettositaStrutturale]);

  useEffect(() => {
    const { difettositaStrutturale } = calculateDifettositaComplessiva(
      riepilogoStrutturaTable
    );
    console.log(riepilogoStrutturaTable, 'RIEPILOGO STRUTTURA TABLE');
    if (!difettositaStrutturaleEffettiva || !difettositaStrutturale) return;
    if (difettositaStrutturaleEffettiva === difettositaStrutturale) {
      setValue(
        'arbitraryStructuralFoundationalDefectiveness',
        difettositaStrutturale
      );
    }
  }, [difettositaStrutturaleEffettiva, riepilogoStrutturaTable]);

  useEffect(() => {
    if (
      previousSismica.current === undefined &&
      !difettositaSismicaEffettiva &&
      difettositaSismica
    ) {
      setValue('arbitrarySeismicDefectiveness', difettositaSismica);
    } else if (difettositaSismicaEffettiva === previousSismica.current) {
      setValue('arbitrarySeismicDefectiveness', difettositaSismica);
    }

    previousSismica.current = difettositaSismica;
  }, [difettositaSismica]);

  useEffect(() => {
    const { difettositaSismica } = calculateDifettositaComplessiva(
      riepilogoStrutturaTable
    );
    if (!difettositaSismicaEffettiva || !difettositaSismica) return;
    if (difettositaSismicaEffettiva === difettositaSismica) {
      setValue('arbitrarySeismicDefectiveness', difettositaSismica);
    }
  }, [difettositaSismicaEffettiva, riepilogoStrutturaTable]);

  return (
    <>
      <GridWrap container spacing={2} padding="20px" marginTop={-40}>
        <InfoWrapper
          size={3}
          infoDrawer={true}
          drawerTitle={INFO.campateSoggetteACollasso.drawerTitle}
          drawerText={INFO.campateSoggetteACollasso.drawerText}
          legislationMessage={INFO.campateSoggetteACollasso.legislationMessage}
        >
          <RHFSelect
            name="campateSoggetteACollasso"
            label={'Campate soggette a collasso*'}
            defaultValue={''}
            disabled={inspectionDisabled || isViewingInspection}
            selectOptions={[
              {
                label: '≤3',
                value: CampateSoggetteACollasso.LessEqualThree,
              },
              {
                label: '>3',
                value: CampateSoggetteACollasso.GreaterThree,
              },
            ]}
          />
        </InfoWrapper>
      </GridWrap>

      <GridWrap container spacing={2} padding="20px" marginTop={-40}>
        <InfoWrapper
          isDivider={true}
          size={12}
          infoDrawer={true}
          drawerTitle={
            INFO.definizioneElementiSottostrutturaSovrastruttura.drawerTitle
          }
          drawerText={
            INFO.definizioneElementiSottostrutturaSovrastruttura.drawerText
          }
          legislationMessage={
            INFO.definizioneElementiSottostrutturaSovrastruttura
              .legislationMessage
          }
        >
          <Divider>
            Definizione elementi di sottostruttura e sovrastruttura
          </Divider>
        </InfoWrapper>

        <GridWrap item xs={12} sx={{ marginBottom: 1 }}>
          <TabellaRiepilogativa />
        </GridWrap>

        <InfoWrapper
          size={3}
          infoDrawer={true}
          drawerTitle={INFO.difettositaStrutturaleEffettiva.drawerTitle}
          drawerText={INFO.difettositaStrutturaleEffettiva.drawerText}
          legislationMessage={
            INFO.difettositaStrutturaleEffettiva.legislationMessage
          }
        >
          <RHFSelect
            name="arbitraryStructuralFoundationalDefectiveness"
            label={'Difettosità strutturale effettiva'}
            //defaultValue={difettositaStrutturale}
            selectOptions={difettositaOptions}
            //showNoValue={false}
            key={difettositaStrutturaleEffettiva} // forzatura per resettare il campo quando cambia la difettosità strutturale calcolata
          />
        </InfoWrapper>

        <InfoWrapper
          size={3}
          infoDrawer={true}
          drawerTitle={INFO.difettositaSismicaEffettiva.drawerTitle}
          drawerText={INFO.difettositaSismicaEffettiva.drawerText}
          legislationMessage={
            INFO.difettositaSismicaEffettiva.legislationMessage
          }
        >
          <RHFSelect
            name="arbitrarySeismicDefectiveness"
            label={'Difettosità sismica effettiva'}
            // defaultValue={difettositaSismica}
            selectOptions={difettositaOptions}
            // showNoValue={false}
            key={difettositaSismicaEffettiva}
          />
        </InfoWrapper>

        <GridWrap item xs={6}></GridWrap>

        <InfoWrapper
          size={3}
          infoDrawer={true}
          drawerTitle={INFO.vulnerabilitaStrutturaleBassa.drawerTitle}
          drawerText={INFO.vulnerabilitaStrutturaleBassa.drawerText}
          legislationMessage={
            INFO.vulnerabilitaStrutturaleBassa.legislationMessage
          }
        >
          <RHFSelect
            name="vulnerabilitaStrutturaleBassa"
            label={'Vulnerabilità strutturale Bassa'}
            defaultValue={''}
            disabled={
              watch('riepilogoStrutturaTable')?.some(
                el =>
                  !el?.difettositaStrutturale ||
                  el?.difettositaStrutturale == CDAStrutturale.Alta ||
                  el?.difettositaStrutturale == CDAStrutturale.MedioAlta ||
                  el?.difettositaStrutturale == CDAStrutturale.Media
              ) ||
              watchNormaProgetto !== ClasseProgetto.ClasseC ||
              !watchNormaProgetto ||
              inspectionDisabled ||
              isViewingInspection
            }
            selectOptions={[
              {
                label: 'Sì',
                value: VulnerabilitaStrutturaleBassa.Si,
              },
              {
                label: 'No',
                value: VulnerabilitaStrutturaleBassa.No,
              },
            ]}
          />
        </InfoWrapper>
      </GridWrap>

      <ElVulnerabilitaSismica />

      <ElCriticiVulnerabilitaStrutturale />

      <ElCriticiVulnerabilitaSismica />

      <CondCriticheVulnerabilitaStruttura />

      <CondCriticheVulnerabilitaSismica />

      <Divider>Osservazioni</Divider>
      <GridWrap container spacing={2} padding="20px">
        <InfoWrapper
          size={12}
          infoMessage={
            INFO.annotazioniAspettiCriticiRischioStrutturaleSismico.infoMessage
          }
        >
          <RHFTextField
            name="annotazioniRischioStrutturaleESismico"
            label={'Annotazioni rischio strutturale e sismico'}
            inputProps={{ maxLength: 5000 }}
            multiline
            defaultValue=""
            disabled={inspectionDisabled || isViewingInspection}
          />
        </InfoWrapper>

        <GridWrap item md={4}>
          <MediaUpload
            title={`Foto di annotazione rischio strutturale e sismico`}
            btnTitle="Allega file"
            formName="fotoDiAnnotazioneRischioStrutturaleESismico"
            infoPanel="Per un'ottimizzazione nel report, si consiglia di eseguire foto in orizzontale in rapporto 4:3."
            dataType="video/*,image/*"
            preview={true}
            uploadType={UploadMedia.media}
            type={FieldType.images}
            mediaGroup={[MediaGroup.Struttura]}
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
      </GridWrap>
    </>
  );
};

export { Accordion5CriticalAspectsL1 };
